import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { LaborRatePreset } from '@jobos/shared';
import { useToast } from '@/hooks/use-toast';

interface LaborRatePresetsResponse {
  items: LaborRatePreset[];
}

interface LaborRatePresetResponse {
  item: LaborRatePreset;
}

export const laborRatePresetKeys = {
  all: ['labor-rate-presets'] as const,
};

export function useLaborRatePresets() {
  return useQuery({
    queryKey: laborRatePresetKeys.all,
    queryFn: async () => {
      const response = await apiClient.get<LaborRatePresetsResponse>('/labor-rate-presets');
      return response.items;
    },
  });
}

export function useAddLaborRatePreset() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ label, rate, rateType = 'hourly', categoryKey = 'labor', unitKey }: {
      label: string;
      rate: number;
      rateType?: 'hourly' | 'daily';
      categoryKey?: string;
      unitKey?: string;
    }) => {
      const response = await apiClient.post<LaborRatePresetResponse>('/labor-rate-presets', { label, rate, rateType, categoryKey, unitKey });
      return response.item;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: laborRatePresetKeys.all });
      toast({ title: 'Preset added' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add preset.',
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateLaborRatePreset() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, label, rate, rateType, categoryKey, unitKey }: {
      id: string;
      label?: string;
      rate?: number;
      rateType?: 'hourly' | 'daily';
      categoryKey?: string;
      unitKey?: string | null;
    }) => {
      const response = await apiClient.put<LaborRatePresetResponse>(`/labor-rate-presets/${id}`, { label, rate, rateType, categoryKey, unitKey });
      return response.item;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: laborRatePresetKeys.all });
      toast({ title: 'Preset updated' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update preset.',
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteLaborRatePreset() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/labor-rate-presets/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: laborRatePresetKeys.all });
      toast({ title: 'Preset deleted' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete preset.',
        variant: 'destructive',
      });
    },
  });
}
